import { dialog } from "../../../nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import saveAs from "../../../saveAs";
import QRCode from "qrcode";
import {
  deleteObjectUseCase,
  updateObjectUseCase,
} from "../../../usecases/object";
import { getCurrentUserUseCase } from "../../../usecases/user";
import DialogSuccess from "./components/DialogSuccess";
import ResignationDetails from "./components/ResignationDetails";
import { formatDateUTC, formatTimeUTC, generateEmailBody } from "./utils/utils";
import { restUseCase } from "../../../usecases/rest";
import { footer, html } from "framer-motion/m";

class ResignationListPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
    getObjectUseCase
  ) {
    super(
      view,
      findObjectUseCase,
      countObjectUseCase,
      deleteObjectUseCase,
      getObjectUseCase
    );
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.getObjectUseCase = getObjectUseCase;
  }

  componentDidMount() {
    const token = this.view.getToken();
    this.init();
    this.getUser(token);
    return this.getObjects();
  }

  init() {
    const user = this.view.getCurrentUser();
    const isRestrictedRole =
      user?.roles[0].id !== "HR" &&
      user?.roles[0].id !== "Admin" &&
      user?.roles[0].id !== "Accounting";

    this.limit = 20;
    this.where = isRestrictedRole
      ? { $or: [{ "recruiter.id": user?.id }, { "coordinator.id": user?.id }] }
      : {};
    this.search = {};
    this.filter = {};
    this.include = [
      "employee",
      "coordinator",
      "coordinator.employee",
      "recruiter",
      "recruiter.employee",
    ];
    this.keys = undefined;
    this.sort = { created: -1 };
    this.progress = true;
    this.reset();
  }

  async getFileAttached(id) {
    const collection = "resignation";
    if (id) {
      try {
        this.view.showProgress();
        const object = await this.getObjectUseCase.execute(collection, id);
        return object;
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);

      for (const obj of objects) {
        if (obj.id) {
          const resignationLetter = await this.getFileAttached(obj.id);
          obj.resignationLetter = resignationLetter.resignationLetter;
        }
      }

      this.objects = this.objects.concat(objects);
      this.view.setTotal(this.objects.length);
      this.view.setObjects(this.objects);
      console.log(this.objects);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  onClickAdd() {
    this.view.navigateTo("/admin/employee/form");
  }

  async getUser(token) {
    try {
      const user = await getCurrentUserUseCase().execute({ session: token });
      if (user && typeof user === "object") {
        this.user = user;
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      this.view.navigateTo("/expired");
    }
  }

  updateUser(user) {
    this.view.showProgress();
    delete user.isMaster;

    updateObjectUseCase()
      .execute("users", user)
      .then((updatedUser) => {
        this.view.hideProgress();
        this.user = updatedUser;
        this.view.showSuccess("Successfully saved!");
      })
      .catch((error) => {
        this.view.hideProgress();
        this.view.showError(error);
      });
  }

  async getEmployeeData(id) {
    const collection = "employees";
    if (id) {
      try {
        this.view.showProgress();
        const object = await this.getObjectUseCase.execute(collection, id);
        return object;
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }
  async handleLastPayDate(object) {
    const collection = "employees";
    const employee = await this.getEmployeeData(object?.employee?.id);
    const employeeData = { ...employee };
    const date = this.view.state.date;
    const employeeName = `${object?.employee?.firstName} ${object?.employee?.lastName}`;
    const lastPayDate = formatDateUTC(new Date(date));
    const email = object?.employee?.email || employeeData?.email;
    const amount = this.view.state.amount;
    const updatedObject = { ...object };
    updatedObject.recruiter = { id: object.recruiter?.id };
    updatedObject.coordinator = { id: object.coordinator?.id };
    updatedObject.employee = { id: object.employee?.id };

    employeeData.lastPayDate = new Date(date).toISOString();
    updatedObject.amount = Number(amount);
    console.log(updatedObject);
    try {
      await this.upsertUseCase.execute(collection, employeeData);
      await this.upsertUseCase.execute("resignation", updatedObject);
      await this.sendEmail(employeeName, lastPayDate, email);

      this.getObjects();
      this.view.setObjects(this.objects);
      this.view.setState({
        resignationDetails: updatedObject,
        showDetails: false,
      });

      dialog.close();
      dialog.fire({
        html: <DialogSuccess message={"Data Saved Successfully"} />,
        footer: false,
      });
    } catch (error) {
      console.warn(`Error updating employee data: ${error}`);
    }
  }

  async handleResignationAction(object, actionType) {
    const collection = "resignation";
    const user = this.view.getCurrentUser();
    const role = user?.roles?.[0]?.id;
    const remarks = this.view.state.remarks;
    const updatedObject = { ...object };
    const employee = await this.getEmployeeData(object?.employee?.id);
    const employeeData = { ...employee };
    const date = this.view.state.date;

    const statusUpdate = actionType === "approve" ? "Approved" : "Disapproved";
    const inProgressStatus = "In Progress";

    const roleUpdates = {
      Recruiter: "recruiterStatus",
      Coordinator: "coordinatorStatus",
      HR: "hrStatus",
      Accounting: "accountingStatus",
    };
    const roleRemarks = {
      Recruiter: "recruiterRemarks",
      Coordinator: "coordinatorRemarks",
      HR: "hrRemarks",
      Accounting: "accountingRemarks",
    };

    if (role in roleUpdates) {
      updatedObject[roleUpdates[role]] = statusUpdate;
      updatedObject.status = inProgressStatus;
    }
    if (role in roleRemarks) {
      updatedObject[roleRemarks[role]] = remarks;
    }

    if (role === "Accounting") {
      if (actionType === "approve") {
        employeeData.applicant = { id: employeeData?.applicant?.id };
        employeeData.coordinator = { id: employeeData?.coordinator?.id };
        employeeData.recruiter = { id: employeeData?.recruiter?.id };
        employeeData.status = "For Last Pay";
      }
    }

    if (role === "Admin") {
      updatedObject.status = statusUpdate;
      updatedObject.dueDate = new Date().toISOString();
      updatedObject.adminRemarks = remarks;
      if (actionType === "approve") {
        employeeData.applicant = { id: employeeData?.applicant?.id };
        employeeData.coordinator = { id: employeeData?.coordinator?.id };
        employeeData.recruiter = { id: employeeData?.recruiter?.id };
        updatedObject.separationDate = new Date(date).toISOString();
      }
    }

    updatedObject.recruiter = { id: object.recruiter?.id };
    updatedObject.coordinator = { id: object.coordinator?.id };

    console.log(employeeData);
    try {
      const resignation = await this.upsertUseCase.execute(
        collection,
        updatedObject
      );

      if (role === "Admin" || role === "Accounting") {
        await this.upsertUseCase.execute("employees", employeeData);
      }

      this.objects = this.objects.map((obj) =>
        obj.id === resignation.id ? resignation : obj
      );

      this.getObjects();
      this.view.setObjects(this.objects);
      this.view.setState({
        resignationDetails: updatedObject,
        showDetails: false,
      });

      dialog.close();
      dialog.fire({
        html: (
          <DialogSuccess
            message={`${
              actionType === "approve" ? "Approved" : "Disapproved"
            } Successfully.`}
          />
        ),
        footer: false,
      });
    } catch (error) {
      console.error(`${actionType}:`, error);
      this.view.showError(error);
    }
  }

  async onSubmitApprove(object) {
    await this.handleResignationAction(object, "approve");
  }

  async onSubmitReject(object) {
    await this.handleResignationAction(object, "reject");
  }

  showImage() {
    dialog.fire({
      html: (
        <>
          <div className="d-flex justify-content-center mt-2">
            <img
              src="/emailLoading2.gif"
              alt="loading"
              style={{ width: "200px", height: "200px" }}
            />
          </div>
          <div className="text-center mt-3">
            <p style={{ color: "#D18237" }}>Sending email... Please wait...</p>
          </div>
        </>
      ),
      footer: false,
    });
  }

  async sendEmail(employeeName, lastPayDate, getApplicantEmail) {
    dialog.close();
    const method = "POST";
    const path = "/send-email/d16bcf82-2a04-4eb0-bff8-139d65a4ae56";

    const obj = {
      to: getApplicantEmail,
      subject: "Schedule for Releasing Final Pay",
      body: generateEmailBody(employeeName, lastPayDate),
    };

    try {
      this.showImage();
      const response = await restUseCase().execute(method, path, { body: obj });

      if (response) {
        dialog.close();
        this.view.setState({ object: this.object });
        this.view.successModal(response.message);
      }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  }

  async generateQRCode(data) {
    try {
      return await QRCode.toDataURL(data);
    } catch (err) {
      console.error("Error generating QR Code", err);
      return null;
    }
  }

  async getExitInterview() {
    const { resignationDetails } = this.view.state;
    const collection = "exit_interview";
    console.warn("object", resignationDetails);
    const id = resignationDetails?.employee?.exit_interview?.id;

    try {
      this.view.showLoading();

      const object = await this.getObjectUseCase.execute(collection, id);

      this.view.setState({ exitInterview: object });
      this.view.hideLoading();
    } catch (error) {
      this.view.hideLoading();
      console.error("Failed to fetch exit interview: ", error);
    }
  }
}

export default ResignationListPresenter;
